import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { searchThreads } from "../../../api/threads/threadsApi";
import { appPaths } from "../../../shared/routing/appPaths";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import { useLoad } from "../../../shared/loading/useLoad";
import { parseThreadEntryRef } from "../../../shared/references/threadReferences";
import LoadStatus from "../../../ui/feedback/LoadStatus";

interface ThreadSearchResultsProps {
  query: string;
  nameOf: (userId: string) => string;
}

/** Entries and titles matching the query, in every project the user may read (T-13). */
export default function ThreadSearchResults({ query, nameOf }: ThreadSearchResultsProps) {
  const { t, i18n } = useTranslation();
  const load = useCallback((signal: AbortSignal) => searchThreads(query, signal), [query]);
  const { state, reload } = useLoad(load);

  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("threads.search.searching")} onRetry={reload} compact />;
  if (state.value.length === 0) {
    return <p className="app-text-muted px-2 py-3 text-xs">{t("threads.search.none", { query })}</p>;
  }
  return (
    <ul className="space-y-1" aria-label={t("threads.search.results")}>
      {state.value.map((hit) => {
        const entry = hit.entry === null ? null : parseThreadEntryRef(hit.entry);
        return (
          <li key={hit.entry ?? `title:${hit.threadId}`}>
            <Link to={appPaths.thread(hit.threadId, entry?.number ?? null)} className="app-nav-item block space-y-0.5 px-2.5 py-2 text-xs">
              <span className="app-text block truncate text-sm font-medium">{hit.threadTitle}</span>
              <span className="app-text-muted line-clamp-2 block">{hit.snippet}</span>
              <span className="app-text-muted block text-[11px]">
                {entry === null ? t("threads.search.titleMatch") : t("threads.search.entry", { number: entry.number })}
                {hit.authorUserId === null ? null : ` · ${nameOf(hit.authorUserId)}`} · {formatTimeAgo(hit.at, i18n.resolvedLanguage)}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}
